import React, { useEffect, useState, useCallback } from "react";
import { Table, TableColumn, Link, Progress } from "@backstage/core-components";
import { Chip } from "@material-ui/core";
import { PriorityChip } from "./PriorityChip";
import { OpsgenieIncident } from "@internal/plugin-opsgenie-api";
import { DatadogIncident } from "@internal/plugin-ops-review-common";
// import { IncidentSummary } from "../IncidentSummary";
import { useParsedIncidents } from "../../hooks/useParsedIncidents";
import { useParsedDatadogIncidents } from "../../hooks/useParsedDatadogIncidents";
import { ReviewCheckbox } from "./ReviewCheckbox";
import { opsReviewApiRef } from "../../api/OpsReviewApi";
import { useApi } from "@backstage/core-plugin-api";

// Helper function to determine incident source
const getIncidentSource = (incident: any): string => {
  // Datadog incidents have a commander field, OpsGenie incidents don't
  return "commander" in incident ? "datadog" : "opsgenie";
};

export interface Props {
  opsgenieIncidents?: OpsgenieIncident[];
  datadogIncidents?: DatadogIncident[];
  disabled?: boolean;
  reviewed?: boolean;
}

export const IncidentsTable = ({
  opsgenieIncidents = [],
  datadogIncidents = [],
  disabled,
  reviewed,
}: Props) => {
  const opsReviewApi = useApi(opsReviewApiRef);
  const parsedOpsgenieIncidents = useParsedIncidents({
    incidents: opsgenieIncidents,
  });
  const parsedDatadogIncidents = useParsedDatadogIncidents({
    incidents: datadogIncidents,
  });
  const [filteredIncidents, setFilteredIncidents] = useState<any[]>([]);

  const filterIncidents = useCallback(async () => {
    // Combine both incident types
    const opsgenieData = parsedOpsgenieIncidents?.value || [];
    const datadogData = parsedDatadogIncidents?.value || [];
    const allIncidents = [...opsgenieData, ...datadogData];

    if (allIncidents.length === 0) {
      setFilteredIncidents([]);
      return;
    }

    try {
      const promises = allIncidents.map(async (incident: any) => {
        try {
          const incidentResponse = await opsReviewApi.getReviewedIncident(
            incident.id
          );
          if (!incidentResponse.data || !incidentResponse.data[0]) {
            return reviewed === false
              ? { ...incident, source: getIncidentSource(incident) }
              : null;
          }
          return incidentResponse.data[0].reviewed === Boolean(reviewed)
            ? { ...incident, source: getIncidentSource(incident) }
            : null;
        } catch {
          return reviewed === false
            ? { ...incident, source: getIncidentSource(incident) }
            : null;
        }
      });

      const results = await Promise.all(promises);
      const filtered = results.filter((item): item is any => item !== null);
      setFilteredIncidents(filtered);
    } catch {
      setFilteredIncidents(
        allIncidents.map((incident) => ({
          ...incident,
          source: getIncidentSource(incident),
        }))
      );
    }
  }, [
    parsedOpsgenieIncidents?.value,
    parsedDatadogIncidents?.value,
    opsReviewApi,
    reviewed,
  ]);

  useEffect(() => {
    filterIncidents();
  }, [filterIncidents]);

  if (parsedOpsgenieIncidents.loading || parsedDatadogIncidents.loading) {
    return <Progress />;
  }

  const columns: TableColumn<any>[] = [
    {
      title: "Reviewed?",
      field: "reviewed",
      width: "10px",
      render: (rowData) => (
        <ReviewCheckbox incidentId={rowData.id} disabled={disabled} />
      ),
    },
    {
      title: "Source",
      field: "source",
      width: "8px",
      render: (rowData) => (
        <Chip
          label={rowData.source === "datadog" ? "DD" : "OG"}
          color={rowData.source === "datadog" ? "primary" : "secondary"}
          size="small"
          title={rowData.source === "datadog" ? "Datadog" : "OpsGenie"}
        />
      ),
    },
    {
      title: "ID",
      field: "tinyId",
      highlight: true,
      width: "10px",
      render: (rowData) => (
        <a href={rowData.links.web} target="_blank">
          #{rowData.tinyId}
        </a>
      ),
    },
    {
      title: "Priority/Severity",
      field: "priority",
      width: "10px",
      render: (rowData) => <PriorityChip priority={rowData.priority} />,
    },
    {
      title: "Status",
      field: "status",
      width: "10px",
      render: (rowData) => (
        <Chip
          label={rowData.status}
          color={
            rowData.status === "open" || rowData.status === "active"
              ? "secondary"
              : "default"
          }
          size="small"
        />
      ),
    },
    {
      title: "Primary Responders",
      field: "responders",
      width: "10%",
      render: (rowData) => {
        let count = 0;

        return rowData.responders
          .map((responder: any): JSX.Element | undefined => {
            const length = rowData.responders.filter(
              (r: any) => !r.filter
            ).length;

            if (responder.filter) {
              return <></>;
            }

            count++;

            return (
              <span key={responder.name || responder.id}>
                <Link
                  to={responder.link || "/catalog"}
                  style={{ display: "inline" }}
                >
                  {responder.name || responder.id}
                </Link>
                {length === count ? "" : ", "}
              </span>
            );
          })
          .filter((c: any) => c);
      },
    },
    {
      title: "Updated At",
      field: "updatedAt",
      type: "datetime",
      width: "10%",
      dateSetting: { locale: "en-US" },
    },
    {
      title: "Description",
      field: "message",
      width: "15%",
    },
  ];

  return (
    <Table
      options={{
        sorting: true,
        search: true,
        paging: true,
        actionsColumnIndex: -1,
        pageSize: 5,
        pageSizeOptions: [5, 10, 15, 20, 25],
        padding: "dense",
      }}
      localization={{ header: { actions: undefined } }}
      columns={columns}
      data={filteredIncidents || []}
      style={{
        fontSize: "1.2em",
        width: "100%",
      }}
    />
  );
};
